"use client";

import { IconTile } from "@cmp-src/data-display/IconTile";
import { WorkplaceRequestRelated } from "@gettactic/api";
import type { IconValue } from "@gettactic/components/src/icons";
import { cn } from "@gettactic/helpers";
import { ChevronRight } from "lucide-react";
import React from "react";
import { classes } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/utils";

// Dot tint by base status, matching the status select's legend.
const dotColor = (status: string): string =>
  status === "pending"
    ? "bg-amber-500"
    : status === "closed"
      ? "bg-green-500"
      : "bg-indigo-500";

interface ParentRequestCardProps {
  request: WorkplaceRequestRelated;
  typeName: string | null;
  typeIcon: IconValue | null;
  typeColor: string;
  onOpen: () => void;
}

/**
 * Where a spawned request came from. A follow-up filed by a workflow reads as
 * an orphan otherwise: the sidebar is the one place the request's own context
 * lives, so the request that created it belongs there too.
 */
export const ParentRequestCard: React.FC<ParentRequestCardProps> = ({
  request,
  typeName,
  typeIcon,
  typeColor,
  onOpen
}) => {
  const status = request.sub_status?.name ?? request.status;

  return (
    <div className="border-neutral-200 border-t pt-5">
      <div className={classes.label}>Parent request</div>
      <button
        type="button"
        onClick={onOpen}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2.5 rounded-brand px-2 py-2 text-left transition-colors hover:bg-neutral-100"
      >
        <IconTile color={typeColor} icon={typeIcon} size="sm" bordered />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-inter font-semibold text-neutral-900 text-sm">
            {request.title}
          </span>
          {/* The sidebar is 320px wide and a type name can be long, so the
              number and status sit on their own line rather than being the
              first thing an ellipsis eats. */}
          {typeName ? (
            <span className="truncate font-inter text-neutral-500 text-xs">
              {typeName}
            </span>
          ) : null}
          <span className="flex min-w-0 items-center gap-1.5 font-inter text-neutral-500 text-xs">
            {request.display_id ? (
              <span className="shrink-0">REQ-{request.display_id} ·</span>
            ) : null}
            <span
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full",
                dotColor(request.status)
              )}
            />
            <span className="truncate">{status}</span>
          </span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-neutral-400" />
      </button>
    </div>
  );
};
